<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ScheduledMessageModal from './ScheduledMessageModal.vue';
import ScheduledMessageSkeletonLoader from './ScheduledMessageSkeletonLoader.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const alert = useAlert;

const uiFlags = useMapGetter('scheduledMessages/getUIFlags');
const recurringUiFlags = computed(() => store.getters['recurringScheduledMessages/getUIFlags'] || { isFetching: false, isCreating: false, isUpdating: false, isDeleting: false });

const showModal = ref(false);
const editingMessage = ref(null);
const isEditingRecurring = ref(false);
const activeTab = ref('scheduled');
const scheduledFilter = ref('all');
const recurringFilter = ref('all');

const isFetching = computed(() => uiFlags.value.isFetching || recurringUiFlags.value.isFetching);
const isCreating = computed(() => uiFlags.value.isCreating || recurringUiFlags.value.isCreating);
const isUpdating = computed(() => uiFlags.value.isUpdating || recurringUiFlags.value.isUpdating);
const isDeleting = computed(() => uiFlags.value.isDeleting || recurringUiFlags.value.isDeleting);
const isSubmitting = computed(() => isCreating.value || isUpdating.value);

const scheduledMessages = computed(() => {
  const messages = store.getters['scheduledMessages/getAllByConversation'](props.conversationId) || [];
  if (scheduledFilter.value === 'all') return messages;
  return messages.filter(m => m.status === scheduledFilter.value);
});

const recurringMessages = computed(() => {
  const messages = store.getters['recurringScheduledMessages/getAllByConversation'](props.conversationId) || [];
  if (recurringFilter.value === 'all') return messages;
  return messages.filter(m => m.status === recurringFilter.value);
});

const filteredMessages = computed(() => {
  return activeTab.value === 'scheduled' ? scheduledMessages.value : recurringMessages.value;
});

const scheduledCount = computed(() => {
  return (store.getters['scheduledMessages/getAllByConversation'](props.conversationId) || []).length;
});

const recurringCount = computed(() => {
  return (store.getters['recurringScheduledMessages/getAllByConversation'](props.conversationId) || []).length;
});

const currentFilterStatuses = computed(() => {
  return activeTab.value === 'scheduled'
    ? ['all', 'pending', 'sent', 'failed', 'draft']
    : ['all', 'active', 'paused', 'completed', 'cancelled'];
});

const filterStatus = computed(() => {
  return activeTab.value === 'scheduled' ? scheduledFilter.value : recurringFilter.value;
});

const setFilter = status => {
  if (activeTab.value === 'scheduled') {
    scheduledFilter.value = status;
  } else {
    recurringFilter.value = status;
  }
};

const filterColorMap = {
  all: {
    active: 'bg-n-primary-100 border-n-primary-300 text-n-primary-700 dark:bg-n-primary-900/30 dark:border-n-primary-700 dark:text-n-primary-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  pending: {
    active: 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  sent: {
    active: 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  failed: {
    active: 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  draft: {
    active: 'bg-gray-100 border-gray-300 text-gray-700 dark:bg-gray-700/30 dark:border-gray-600 dark:text-gray-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  held: {
    active: 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  active: {
    active: 'bg-blue-100 border-blue-300 text-blue-700 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  paused: {
    active: 'bg-yellow-100 border-yellow-300 text-yellow-700 dark:bg-yellow-900/30 dark:border-yellow-700 dark:text-yellow-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  completed: {
    active: 'bg-green-100 border-green-300 text-green-700 dark:bg-green-900/30 dark:border-green-700 dark:text-green-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
  cancelled: {
    active: 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300',
    inactive: 'bg-white border-n-divider text-n-text dark:bg-transparent dark:border-white/10 dark:text-n-text-white',
  },
};

const getFilterClasses = status => {
  const isSelected = filterStatus.value === status;
  const colors = filterColorMap[status] || filterColorMap.all;
  return isSelected ? colors.active : colors.inactive;
};

const scheduledStatusLabels = {
  all: t('SCHEDULED_MESSAGES.STATUS.ALL'),
  pending: t('SCHEDULED_MESSAGES.STATUS.PENDING'),
  sent: t('SCHEDULED_MESSAGES.STATUS.SENT'),
  failed: t('SCHEDULED_MESSAGES.STATUS.FAILED'),
  draft: t('SCHEDULED_MESSAGES.STATUS.DRAFT'),
  held: t('SCHEDULED_MESSAGES.STATUS.HELD'),
};

const recurringStatusLabels = {
  all: t('SCHEDULED_MESSAGES.STATUS.ALL'),
  active: t('RECURRING_SCHEDULED_MESSAGES.STATUS.ACTIVE'),
  paused: t('RECURRING_SCHEDULED_MESSAGES.STATUS.PAUSED'),
  completed: t('RECURRING_SCHEDULED_MESSAGES.STATUS.COMPLETED'),
  cancelled: t('RECURRING_SCHEDULED_MESSAGES.STATUS.CANCELLED'),
  draft: t('RECURRING_SCHEDULED_MESSAGES.STATUS.DRAFT'),
};

const currentStatusLabels = computed(() => {
  return activeTab.value === 'scheduled' ? scheduledStatusLabels : recurringStatusLabels;
});

const formatDateTime = timestamp => {
  if (!timestamp) return '';
  const date = typeof timestamp === 'number'
    ? new Date(timestamp * 1000)
    : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} - ${hours}:${minutes}`;
};

const openCreateModal = () => {
  editingMessage.value = null;
  isEditingRecurring.value = false;
  showModal.value = true;
};

const openEditModal = (message, recurring = false) => {
  editingMessage.value = recurring ? { ...message, id: `recurring-${message.id}` } : message;
  isEditingRecurring.value = recurring;
  showModal.value = true;
};

const handleDelete = async (id, isRecurring = false) => {
  if (!confirm(t('SCHEDULED_MESSAGES.CONFIRM_DELETE'))) return;

  try {
    if (isRecurring) {
      await store.dispatch('recurringScheduledMessages/delete', {
        conversationId: props.conversationId,
        recurringScheduledMessageId: id,
      });
    } else {
      await store.dispatch('scheduledMessages/delete', {
        conversationId: props.conversationId,
        scheduledMessageId: id,
      });
    }
    alert(t('SCHEDULED_MESSAGES.DELETED_SUCCESS'));
  } catch (error) {
    console.error('Failed to delete scheduled message:', error);
    alert(t('SCHEDULED_MESSAGES.ERRORS.DELETE_FAILED'));
  }
};

const handleCreated = () => {
  alert(t('SCHEDULED_MESSAGES.CREATED_SUCCESS'));
};

onMounted(() => {
  store.dispatch('scheduledMessages/get', { conversationId: props.conversationId });
  store.dispatch('recurringScheduledMessages/get', { conversationId: props.conversationId });
});

watch(
  () => props.conversationId,
  () => {
    store.dispatch('scheduledMessages/get', { conversationId: props.conversationId });
    store.dispatch('recurringScheduledMessages/get', { conversationId: props.conversationId });
  }
);
</script>

<template>
  <div class="flex flex-col bg-white dark:bg-[#1e1e2e] border border-black/5 dark:border-white/10 shadow-[0_0_15px_rgba(255,255,255,0.03)] rounded-xl overflow-hidden h-[600px]">
    <!-- Header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-divider dark:border-white/10">
      <div>
        <h2 class="text-lg font-semibold text-n-text dark:text-n-text-white">
          {{ t('SCHEDULED_MESSAGES.TITLE') }}
        </h2>
      </div>
      <div class="flex items-center gap-2">
        <NextButton @click="openCreateModal" :disabled="isSubmitting" :loading="isCreating">
          <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
          {{ t('SCHEDULED_MESSAGES.NEW') }}
        </NextButton>
        <button
          type="button"
          class="p-2 text-n-text-light hover:text-n-text dark:text-n-text-muted dark:hover:text-n-text-white transition-colors"
          @click="$emit('close')"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-n-divider dark:border-white/10">
      <button
        v-for="tab in [
          { key: 'scheduled', label: t('SCHEDULED_MESSAGES.TAB_SCHEDULED'), count: scheduledCount },
          { key: 'recurring', label: t('SCHEDULED_MESSAGES.TAB_RECURRING'), count: recurringCount },
        ]"
        :key="tab.key"
        type="button"
        class="flex-1 px-4 py-3 text-sm font-medium transition-colors relative"
        :class="{
          'text-n-primary-600 dark:text-n-primary-400': activeTab === tab.key,
          'text-n-text-light dark:text-n-text-muted hover:text-n-text dark:hover:text-n-text-white': activeTab !== tab.key,
        }"
        @click="activeTab = tab.key"
      >
        {{ tab.label }} ({{ tab.count }})
        <div
          v-if="activeTab === tab.key"
          class="absolute bottom-0 left-0 right-0 h-0.5 bg-n-primary-500"
        />
      </button>
    </div>

    <!-- Filters -->
    <div class="px-4 py-2 border-b border-n-divider dark:border-white/10 bg-n-surface-1 dark:bg-white/5">
      <div class="flex flex-wrap gap-2">
        <button
          v-for="status in currentFilterStatuses"
          :key="status"
          type="button"
          class="px-3 py-1.5 text-sm rounded-lg border transition-colors"
          :class="getFilterClasses(status)"
          @click="setFilter(status)"
        >
          {{ currentStatusLabels[status] }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-4">
      <div v-if="isFetching && filteredMessages.length === 0">
        <ScheduledMessageSkeletonLoader :count="5" />
      </div>

      <div
        v-else-if="filteredMessages.length === 0"
        class="flex flex-col items-center justify-center py-12 text-n-text-light dark:text-n-text-muted"
      >
        <svg class="w-16 h-16 mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p class="text-center">{{ t('SCHEDULED_MESSAGES.EMPTY') }}</p>
        <NextButton @click="openCreateModal" class="mt-4">
          {{ t('SCHEDULED_MESSAGES.NEW') }}
        </NextButton>
      </div>

      <div v-else class="space-y-3">
        <!-- Scheduled Messages -->
        <template v-if="activeTab === 'scheduled'">
          <div
            v-for="msg in scheduledMessages"
            :key="msg.id"
            class="flex items-center gap-3 p-3 bg-white dark:bg-white/5 rounded-lg border border-n-divider dark:border-white/10 hover:bg-n-surface-1/50 dark:hover:bg-white/8 transition-colors"
          >
            <div class="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              :class="{
                'bg-blue-100 dark:bg-blue-900/30': msg.status === 'pending',
                'bg-green-100 dark:bg-green-900/30': msg.status === 'sent',
                'bg-red-100 dark:bg-red-900/30': msg.status === 'failed',
                'bg-gray-100 dark:bg-gray-700': msg.status === 'draft',
                'bg-yellow-100 dark:bg-yellow-900/30': msg.status === 'held',
              }"
            >
              <svg v-if="msg.status === 'pending'" class="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <svg v-else-if="msg.status === 'sent'" class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
              <svg v-else-if="msg.status === 'failed'" class="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              <svg v-else class="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-xs font-mono text-n-text-light dark:text-n-text-muted">#{{ msg.id }}</span>
                <p class="text-sm font-medium text-n-text dark:text-n-text-white truncate">
                  {{ msg.content?.substring(0, 80) }}{{ msg.content?.length > 80 ? '...' : '' }}
                </p>
              </div>
              <div class="flex items-center gap-3 mt-1 text-xs text-n-text-light dark:text-n-text-muted">
                <span
                  class="px-2 py-0.5 rounded-full text-xs"
                  :class="{
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': msg.status === 'pending',
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': msg.status === 'sent',
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': msg.status === 'failed',
                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300': msg.status === 'draft',
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': msg.status === 'held',
                  }"
                >
                  {{ scheduledStatusLabels[msg.status] || msg.status }}
                </span>
                <span v-if="msg.scheduled_at">
                  {{ formatDateTime(msg.scheduled_at) }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-1">
              <button
                type="button"
                class="p-1.5 text-n-text-light hover:text-n-text dark:hover:text-n-text-white transition-colors"
                @click="openEditModal(msg, false)"
                :disabled="isSubmitting"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                type="button"
                class="p-1.5 text-red-500 hover:text-red-600 transition-colors"
                @click="() => handleDelete(msg.id)"
                :disabled="isSubmitting || msg.status === 'sent' || msg.status === 'failed'"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            </div>
          </div>
        </template>

        <!-- Recurring Messages -->
        <template v-else>
          <div
            v-for="msg in recurringMessages"
            :key="msg.id"
            class="flex items-center gap-3 p-3 bg-white dark:bg-white/5 rounded-lg border border-n-divider dark:border-white/10 hover:bg-n-surface-1/50 dark:hover:bg-white/8 transition-colors"
          >
            <div class="w-10 h-10 rounded-full bg-n-primary-100 dark:bg-n-primary-900/30 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-n-primary-600 dark:text-n-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2">
                <span class="text-xs font-mono text-n-text-light dark:text-n-text-muted">#{{ msg.id }}</span>
                <p class="text-sm font-medium text-n-text dark:text-n-text-white truncate">
                  {{ msg.content?.substring(0, 80) }}{{ msg.content?.length > 80 ? '...' : '' }}
                </p>
              </div>
              <div class="flex items-center gap-3 mt-1 text-xs text-n-text-light dark:text-n-text-muted">
                <span
                  class="px-2 py-0.5 rounded-full text-xs"
                  :class="{
                    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300': msg.status === 'active',
                    'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300': msg.status === 'paused',
                    'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300': msg.status === 'draft',
                    'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300': msg.status === 'completed',
                    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300': msg.status === 'cancelled',
                  }"
                >
                  {{ recurringStatusLabels[msg.status] || msg.status }}
                </span>
                <span v-if="msg.recurrence_rule">{{ msg.recurrence_description }}</span>
                <span v-if="msg.occurrences_sent > 0">
                  {{ t('RECURRING_SCHEDULED_MESSAGES.OCCURRENCES_SENT', { count: msg.occurrences_sent }) }}
                </span>
              </div>
              <div class="flex items-center gap-2 mt-1">
                <Switch
                  :model-value="msg.status === 'active'"
                  :disabled="recurringUiFlags.isUpdating"
                  @update:modelValue="val => store.dispatch('recurringScheduledMessages/update', {
                    conversationId: props.conversationId,
                    recurringScheduledMessageId: msg.id,
                    payload: { status: val ? 'active' : 'paused' }
                  })"
                />
                <button
                  type="button"
                  class="p-1.5 text-n-text-light hover:text-n-text dark:hover:text-n-text-white transition-colors"
                  @click="openEditModal(msg, true)"
                  :disabled="recurringUiFlags.isUpdating"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="p-1.5 text-red-500 hover:text-red-600 transition-colors"
                  @click="() => handleDelete(msg.id, true)"
                  :disabled="recurringUiFlags.isDeleting"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>

    <!-- Modal -->
    <ScheduledMessageModal
      v-model:show="showModal"
      :conversation-id="props.conversationId"
      :inbox-id="props.inboxId"
      :scheduled-message="editingMessage"
      :initial-recurring="isEditingRecurring || activeTab === 'recurring'"
      @close="showModal = false"
      @scheduledMessageCreated="handleCreated"
    />
  </div>
</template>